// import { Form, Input, Select, Button, DatePicker, Divider, Typography, Modal, message } from "antd";
import { apiRequest } from "../../utils/apiClient";
import checkPosType from "../../utils/posType";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Divider,
  DatePicker,
  Row,
  Col,
  Typography,
  message,
  Popover,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  HS_CODE_HINTS,
  UOM_OPTIONS,
  UOM_NUMBERS_PIECES_UNITS,
  SALES_TAX_RATES,
  DEFAULT_CATEGORY,
  INVOICE_TYPE_DIGITAL_INVOICING,
  INVOICE_TYPE_POS,
} from "../../utils/constants";
  
const { Title, Text } = Typography;

const Add = ({
  isAddModalOpen,
  setIsAddModalOpen,
  products,
  setProducts,
  categories,
}) => {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const [form] = Form.useForm();
  const [calculatedUnitPrice, setCalculatedUnitPrice] = useState(null);
  const user = useSelector((state) => state.user);

  const userType = user?.invoiceType || null;
  const businessName = user?.businessName || "";
  const posType = checkPosType(businessName);

  const shouldShowPackFields = userType === INVOICE_TYPE_POS && posType === false;

  const calculateUnitPrice = (packPrice, packQuantity) => {
    if (packPrice && packQuantity && Number(packQuantity) > 0) {
      const unitPrice = Number(packPrice) / Number(packQuantity);
      setCalculatedUnitPrice(unitPrice.toFixed(2));
    } else {
      setCalculatedUnitPrice(null);
    }
  };

  const handlePackPriceChange = (e) => {
    const packPrice = e?.target?.value;
    const packQuantity = form.getFieldValue("unit");
    calculateUnitPrice(packPrice, packQuantity);
  };

  const handlePackQuantityChange = (e) => {
    const packQuantity = e?.target?.value;
    const packPrice = form.getFieldValue("price");
    calculateUnitPrice(packPrice, packQuantity);
  };

  const onFinish = async (values) => {
    try {
      const payload = {
        ...values,
        price: Number(values.price) || 0,
        unit: shouldShowPackFields && values.unit ? Number(values.unit) || 0 : undefined,
        totalStock: Number(values.totalStock) || 0,
        mg: values.mg ? Number(values.mg) || 0 : undefined,
        salesTax: Number(String(values.salesTax || "").replace("%", "").trim()) || 0,
      };
      const effectiveBranchId = user?.branchId || user?.selectedBranchId;
      if (effectiveBranchId) payload.branchId = effectiveBranchId;

      const res = await apiRequest("/api/products/add-product", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      message.success(t("productAddedSuccess"));
      setIsAddModalOpen(false);
      form.resetFields();
      setCalculatedUnitPrice(null);

      // Backend returns `{ message, product }`
      const created = res?.product;

      const productToAdd = created && created._id
        ? {
            ...payload,
            ...created,
            title: payload.title || created.title || tCommon("untitled"),
          }
        : null;

      if (productToAdd?._id && productToAdd.title) {
        setProducts([...products.filter((p) => p && p._id), productToAdd]);
      } else {
        message.error(t("productAddedUnexpected"));
      }
    } catch (error) {
      console.error(error);
      message.error(error?.message || t("failedToAddProduct"));
    }
  };

  return (
    <Modal
  title={t("addNewProduct")}
  open={isAddModalOpen}
  onCancel={() => {
    setIsAddModalOpen(false);
    setCalculatedUnitPrice(null);
  }}
  footer={null}
  destroyOnHidden
  width={900}
>
  <Form
    layout="vertical"
    form={form}
    onFinish={onFinish}
    initialValues={{ category: DEFAULT_CATEGORY }}
  >
    <Title level={4}>{t("mandatoryFields")}</Title>
    <Text type="secondary">
      {t("mandatoryFieldsDesc")}
    </Text>
    <Divider />

    <Row gutter={16}>
      <Col span={8}>
        <Form.Item
          label={t("productName")}
          name="title"
          rules={[{ required: true, message: t("fieldRequired") }]}
        >
          <Input placeholder={t("enterProductName")} />
        </Form.Item>
      </Col>

      {shouldShowPackFields ? (
        <>
          <Col span={8}>
            <Form.Item
              label={t("packPrice")}
              name="price"
              rules={[{ required: true, message: t("fieldRequired") }]}
            >
              <Input 
                type="number" 
                placeholder={t("enterPackPrice")} 
                onChange={handlePackPriceChange}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={t("packQuantity")}
              name="unit"
              rules={[{ required: true, message: t("fieldRequired") }]}
              extra={
                calculatedUnitPrice !== null ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("calculatedUnitPrice")}: <strong style={{ color: "#1890ff" }}>{calculatedUnitPrice}</strong>
                  </Text>
                ) : null
              }
            >
              <Input 
                type="number" 
                placeholder={t("enterPackQuantity")} 
                onChange={handlePackQuantityChange}
              />
            </Form.Item>
          </Col>
        </>
      ) : (
        <Col span={8}>
          <Form.Item
            label={t("unitPrice")}
            name="price"
            rules={[{ required: true, message: t("fieldRequired") }]}
          >
            <Input type="number" placeholder={t("enterUnitPrice")} />
          </Form.Item>
        </Col>
      )}

      <Col span={8}>
        <Form.Item
          label={t("totalStock")}
          name="totalStock"
          rules={[{ required: true, message: t("fieldRequired") }]}
        >
          <Input type="number" placeholder={t("enterTotalStock")} />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={8}>
        <Form.Item
          label={t("unitOfMeasurement")}
          name="uom"
          rules={[{ required: true, message: t("uomRequired") }]}
        >
          <Select placeholder={t("selectUomShort")}>
            {UOM_OPTIONS.map((uom) => (
              <Select.Option key={uom} value={uom}>
                {uom === UOM_NUMBERS_PIECES_UNITS
                  ? uom
                  : uom === "MT"
                  ? "Metric Ton (MT)"
                  : uom === "KG"
                  ? "Kilogram (KG)"
                  : uom === "Grams"
                  ? "Grams (G)"
                  : uom === "Packs"
                  ? "Packs"
                  : "Milligram (MG)"}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item
          label={
            <span>
              {t("hsCode")}{" "}
              {posType === false && userType !== INVOICE_TYPE_DIGITAL_INVOICING && (
                <Popover
                  placement="right"
                  title={t("commonHSCodes")}
                  trigger="click"
                  overlayStyle={{ maxWidth: 300 }}
                  content={
                    <div style={{ maxWidth: 280, maxHeight: 400, overflowY: "auto", padding: "4px 0" }}>
                      <div style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
                        {t("clickCodeToFill")}
                      </div>
                      {HS_CODE_HINTS && HS_CODE_HINTS.length > 0 ? (
                        <ul style={{ paddingLeft: 18, margin: 0, fontSize: 12, listStyle: "disc" }}>
                          {HS_CODE_HINTS.map((item) => (
                            <li 
                              key={item.code} 
                              style={{ 
                                marginBottom: 6, 
                                lineHeight: 1.5,
                                cursor: "pointer",
                                color: "#1890ff",
                              }}
                              onClick={() => {
                                form.setFieldsValue({ hscode: item.code });
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.textDecoration = "underline";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.textDecoration = "none";
                              }}
                            >
                              <strong style={{ color: "#1890ff" }}>{item.label}</strong>
                              <span style={{ color: "#1890ff" }}>: {item.code}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div style={{ color: "#999", fontSize: 12 }}>{t("noHSCodesAvailable")}</div>
                      )}
                    </div>
                  }
                >
                  <InfoCircleOutlined style={{ color: "#1890ff", marginLeft: 4, cursor: "pointer" }} />
                </Popover>
              )}
            </span>
          }
          name="hscode"
          rules={[{ required: true, message: t("hscodeRequired") }]}
        >
          <Input type="number" placeholder={t("enterHSCode")} />
        </Form.Item>
      </Col>

      <Col span={8}>
        <Form.Item
          label={t("category")}
          name="category"
          rules={[{ required: true, message: t("fieldRequired") }]}
        >
          <Select
            showSearch
            placeholder={t("selectCategory")}
            optionFilterProp="label"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            options={[
              { value: DEFAULT_CATEGORY, label: DEFAULT_CATEGORY },
              ...categories.map((item) => ({
                value: item.title,
                label: item.title,
              })),
            ]}
          />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={8}>
        <Form.Item
          label={t("salesTax")}
          name="salesTax"
          rules={[{ required: true, message: t("salesTaxRequired") }]}
        >
          <Select placeholder={t("selectTax")}>
            {SALES_TAX_RATES.map((rate) => (
              <Select.Option key={rate} value={rate}>
                {rate}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </Row>

    <Divider />
    <Title level={4}>{t("optionalFields")}</Title>
    <Text type="secondary">
      {t("optionalFieldsDesc")}
    </Text>
    <Divider />

    <Row gutter={16}>
      <Col span={8}>
        <Form.Item label={t("productImage")} name="img">
          <Input placeholder={t("enterImageUrl")} />
        </Form.Item>
      </Col>

      {userType !== INVOICE_TYPE_DIGITAL_INVOICING && posType === false && (
        <>
          <Col span={8}>
            <Form.Item label={t("expiryDate")} name="expiry">
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={t("batchNumber")} name="batchNumber">
              <Input placeholder={t("enterBatchNumber")} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="mg" name="mg">
              <Input type="number" placeholder={t("enterMg")} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={t("barcode")} name="barcode">
              <Input placeholder={t("enterBarcode")} />
            </Form.Item>
          </Col>
        </>
      )}
    </Row>

    <Divider />
    <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
      <Button type="primary" htmlType="submit">
        {t("createProduct")}
      </Button>
    </Form.Item>
  </Form>
</Modal>
  );
};

export default Add;
